import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

// Apply Heineken theme
// Heineken Green #13670B
// Heineken Red #ff2b00
// Heineken White #ffffff
// Heineken Silver #c3c3c3
// Only use light theme for now
// Help me change these below of light theme

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
  isMobile: boolean;
  colors: {
    // Background colors
    bg: {
      primary: string;
      secondary: string;
      tertiary: string;
      accent: string;
    };
    // Text colors
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
      inverse: string;
    };
    // Border colors
    border: {
      primary: string;
      secondary: string;
    };
    // Interactive colors
    interactive: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
      danger: string;
      dangerHover: string;
    };
  };
};

const lightTheme = {
  bg: {
    primary: 'bg-white',             // Clean white background
    secondary: 'bg-gray-50',         // Light gray section backgrounds
    tertiary: 'bg-[#C3C3C3]/20',     // Silver tint for subtle panels
    accent: 'bg-[#13670B]/5',        // Very soft green accent
  },
  text: {
    primary: 'text-[#13670B]',       // Heineken green for main headings
    secondary: 'text-gray-700',      // Standard readable gray
    tertiary: 'text-gray-500',       // Muted text
    inverse: 'text-white',           // Text on green/red buttons
  },
  border: {
    primary: 'border-[#C3C3C3]',     // Silver borders
    secondary: 'border-gray-200',    // Subtle light borders
  },
  interactive: {
    primary: 'bg-[#13670B]',         // Heineken green button
    primaryHover: 'hover:bg-[#0f5409]', // Slightly darker green on hover
    secondary: 'bg-[#C3C3C3]',       // Silver button
    secondaryHover: 'hover:bg-[#a8a8a8]/10', // Darker silver on hover
    danger: 'bg-[#FF2B00]',          // Heineken red for warnings
    dangerHover: 'hover:bg-[#e02700]', // Slightly darker red
  },
};



// old light theme
// const lightTheme = {
//   bg: {
//     primary: 'bg-white',           // Main background
//     secondary: 'bg-gray-50',       // Secondary background
//     tertiary: 'bg-gray-100',       // Cards, panels
//     accent: 'bg-gray-200',         // Subtle accents
//   },
//   text: {
//     primary: 'text-gray-900',      // Main text
//     secondary: 'text-gray-700',    // Secondary text
//     tertiary: 'text-gray-500',     // Muted text
//     inverse: 'text-white',         // Text on dark backgrounds
//   },
//   border: {
//     primary: 'border-gray-200',    // Main borders
//     secondary: 'border-gray-300',  // Subtle borders
//   },
//   interactive: {
//     primary: 'bg-blue-600',
//     primaryHover: 'hover:bg-blue-700',
//     secondary: 'bg-gray-200',
//     secondaryHover: 'hover:bg-gray-300',
//     danger: 'bg-red-600',
//     dangerHover: 'hover:bg-red-700',
//   },
// };

const darkTheme = {
  bg: {
    primary: 'bg-gray-900',        // Main background
    secondary: 'bg-gray-950',      // Secondary background
    tertiary: 'bg-gray-800',       // Cards, panels
    accent: 'bg-gray-700',         // Subtle accents
  },
  text: {
    primary: 'text-gray-200',      // Main text
    secondary: 'text-gray-300',    // Secondary text
    tertiary: 'text-gray-500',     // Muted text
    inverse: 'text-white',         // Text on dark backgrounds
  },
  border: {
    primary: 'border-gray-800',    // Main borders
    secondary: 'border-gray-700',  // Subtle borders
  },
  interactive: {
    primary: 'bg-blue-600',
    primaryHover: 'hover:bg-blue-700',
    secondary: 'bg-gray-800',
    secondaryHover: 'hover:bg-gray-700',
    danger: 'bg-red-600',
    dangerHover: 'hover:bg-red-700',
  },
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light');
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Device detection: check if window width is <= 768px
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setTheme(savedTheme);
    }
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const colors = theme === 'light' ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isMobile, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;